import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import {
    Button,
    Checkbox,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
} from '@material-ui/core';
import PaymentIcon from '@material-ui/icons/Payment';
import IconCancel from '@material-ui/icons/Cancel';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import NumberFormat from 'react-number-format';

import {printContent, showErrorNotify, showSuccessNotify} from '../components';
import axiosInstance from '../api/axios';
import env from '../env';
import BillDetailDialog from './BillDetailDialog';
import QrInfoDialog from './QrInfoDialog';
import Popconfirm from './Popconfirm';
import PaymentMethodPicker from "./PaymentMethodPicker";
import {connect} from "react-redux";
import {refreshView} from "react-admin";

const styles = {
    suggestDialog: {zIndex: 1},
    width100: {width: '100%'},
    nowrap: {whiteSpace: 'nowrap'},
    right: {textAlign: 'right'},
    subtle: {color: '#666'},
    collapseCell: {paddingBottom: 0, paddingTop: 0}
};

const currency = new Intl.NumberFormat('en-EN');
const formatNumber = (n: any) => (n == null ? '0' : currency.format(n));

const PostQuickPayBillButton = ({ record,  refreshView }) => {
    const [open, setOpen] = useState(false);
    const [openDetail, setOpenDetail] = useState(false);
    const [openQr, setOpenQr] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // form state
    const [customerId, setCustomerId] = useState(record?.customerId ?? '');
    const [customerName, setCustomerName] = useState(record?.customerName ?? '');
    const [relationshipName, setRelationshipName] = useState('');
    const [relationshipAddress, setRelationshipAddress] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('BANK_TRANSFER');
    const [gateway, setGateway] = useState('');
    const [paymentDate, setPaymentDate] = useState('');

    // data state
    const [bills, setBills] = useState([]);
    const [remainPaymentAmount, setRemainPaymentAmount] = useState(0);
    const [enableShowPrint, setEnableShowPrint] = useState(true);
    const [enableShowQr, setEnableShowQr] = useState(false);
    const [totalCanPayAmount, setTotalCanPayAmount] = useState(0);
    const [gateways, setGateways] = useState([]);

    // selection (chế độ cũ: chọn bill; chế độ QR: chọn số tháng)
    const [selectedBillIds, setSelectedBillIds] = useState(new Set());
    const [expandedRow, setExpandedRow] = useState(new Set());
    const [numOfMonth, setNumOfMonth] = useState(1);

    // dialogs data
    const [billDetail, setBillDetail] = useState({});
    const [qrInfo, setQrInfo] = useState(null);

    const pollRef = useRef(null);
    const showGateway = paymentMethod === 'BANK_TRANSFER';

    // tổng tiền:
    // - enableShowQr === true → tính theo numOfMonth (cộng N phiếu đầu)
    // - enableShowQr === false → tính theo các bill được check
    const totalPayment = useMemo(() => {
        if (!bills?.length) return 0;

        if (enableShowQr) {
            const n = Math.max(0, Math.min(numOfMonth, bills.length));
            let sum = 0;
            for (let i = 0; i < n; i++) sum += bills[i]?.billBalance || 0;
            return sum;
        }

        if (selectedBillIds.size === 0) return 0;
        let sum = 0;
        for (const b of bills) if (selectedBillIds.has(b.billNo)) sum += (b.billBalance || 0);
        return sum;
    }, [bills, selectedBillIds, enableShowQr, numOfMonth]);

    useEffect(() => () => {
        if (pollRef.current) clearInterval(pollRef.current);
    }, []);

    const resetAll = () => {
        setBills([]);
        setSelectedBillIds(new Set());
        setExpandedRow(new Set());
        setRemainPaymentAmount(0);
        setEnableShowPrint(true);
        setEnableShowQr(false);
        setTotalCanPayAmount(0);
        setGateways([]);
        setCustomerId('');
        setCustomerName('');
        setRelationshipName('');
        setRelationshipAddress('');
        setPaymentMethod('BANK_TRANSFER');
        setGateway('');
        setPaymentDate('');
        setBillDetail({});
        setQrInfo(null);
        setNumOfMonth(1);
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    };

    const openDialog = () => {
        setOpen(true);
        if (record?.customerId) {
            setCustomerId(record.customerId);
            setCustomerName(record.customerName ?? '');
            fetchPaymentInfo(record.customerId);
        }
    };

    const closeDialog = () => {
        resetAll();
        setOpen(false);
    };

    const fetchPaymentInfo = async (cid) => {
        if (!cid) return;
        try {
            const {data} = await axiosInstance.get(`/api/payments/getPaymentInfoV2?customerId=${cid}`);
            setRelationshipName(data.relationshipName || '');
            setRelationshipAddress(data.relationshipAddress || '');
            setPaymentMethod(data.paymentMethod || 'BANK_TRANSFER');
            // lấy danh sách phiếu (ưu tiên availableBills nếu có)
            const list = data.availableBills || data.bills || [];
            setBills(list);
            setRemainPaymentAmount(data.remainPaymentAmount || 0);
            setEnableShowPrint(!!data.enableShowPrint);
            setEnableShowQr(!!data.enableShowQr);        // <-- quan trọng
            setTotalCanPayAmount(data.totalCanPayAmount || 0);
            setGateways(data.gateways || []);

            // reset chọn
            setSelectedBillIds(new Set());
            setExpandedRow(new Set());
            // mặc định khi vào chế độ QR thì numOfMonth = 1
            setNumOfMonth(1);
        } catch {
            showErrorNotify('Không tải được thông tin thanh toán. Vui lòng kiểm tra Customer ID.');
        }
    };

    const validateBeforePay = () => {
        if (!customerId) return showErrorNotify('Vui lòng nhập Mã VinaID'), false;
        if (!relationshipName) return showErrorNotify('Vui lòng nhập Tên người nộp'), false;

        if (enableShowQr) {
            if (!bills.length) return showErrorNotify('Không có phiếu để thanh toán.'), false;
            if (numOfMonth <= 0 || numOfMonth > bills.length) {
                return showErrorNotify('Vui lòng chọn số tháng hợp lệ.'), false;
            }
        } else {
            if (selectedBillIds.size <= 0) return showErrorNotify('Vui lòng chọn ít nhất một phiếu.'), false;
        }

        if (!totalPayment || totalPayment <= 0) return showErrorNotify('Số tiền thanh toán không hợp lệ.'), false;
        if (totalPayment > totalCanPayAmount) return showErrorNotify('Số tiền vượt quá hạn mức có thể thanh toán.'), false;
        return true;
    };

    // Gửi thanh toán:
    // - QR mode: gửi theo numOfMonth (server tính nhóm/phiếu)
    // - Legacy: gửi theo danh sách billNos
    const sendPayment = async () => {
        if (enableShowQr) {
            const payload = {
                customerId,
                payerName: relationshipName,
                payerAddress: relationshipAddress,
                paymentMethod,
                paymentDate,
                gateway,
                numOfMonth,                // <-- quan trọng
                paymentAmount: totalPayment,
            };
            return axiosInstance.post('/api/payments/manual-old', payload);
        } else {
            const payload = {
                customerId,
                payerName: relationshipName,
                payerAddress: relationshipAddress,
                paymentMethod,
                paymentDate,
                gateway,
                billNos: Array.from(selectedBillIds), // giữ như bản cũ
                paymentAmount: totalPayment,
            };
            return axiosInstance.post('/api/payments/manual', payload);
        }
    };

    const afterPaymentSuccess = async (paymentId, printConfirm = false) => {
        showSuccessNotify('Thanh toán thành công!');
        closeDialog();
        if (typeof refreshView === 'function') refreshView();

        if (printConfirm) {
            const receiptUrl = `${env.baseURL}/api/paymentViews/print-both-receipt?paymentId=${paymentId}`;
            try {
                await axiosInstance.get(receiptUrl);
            } catch {
            }
            printContent(`${env.baseURL}/api/paymentViews/print-confirm?paymentId=${paymentId}`);
        } else if (enableShowPrint) {
            printContent(`${env.baseURL}/api/paymentViews/print-both-receipt?paymentId=${paymentId}`);
        } else {
            const receiptUrl = `${env.baseURL}/api/paymentViews/print-both-receipt?paymentId=${paymentId}`;
            try {
                await axiosInstance.get(receiptUrl);
            } catch {
            }
        }
    };

    const handlePay = async (withConfirmPrint = false) => {
        if (!validateBeforePay()) return;
        setIsLoading(true);
        try {
            const {data} = await sendPayment();
            if (!data || !data.paymentId) {
                showErrorNotify('Thanh toán không thành công. Vui lòng thử lại hoặc liên hệ quản trị viên!');
            } else {
                await afterPaymentSuccess(data.paymentId, withConfirmPrint);
            }
        } catch {
            showErrorNotify('Có lỗi xảy ra. Vui lòng liên hệ quản trị viên!');
        } finally {
            setIsLoading(false);
        }
    };

    // Lấy chi tiết:
    // - QR mode → theo numOfMonth
    // - Legacy → theo billNos
    const getPaymentDetail = async () => {
        if (enableShowQr) {
            const url = `/api/payments/getPaymentDetail-old?customerId=${customerId}&numOfMonth=${numOfMonth}`;
            const {data} = await axiosInstance.get(url);
            return data;
        } else {
            const billNos = Array.from(selectedBillIds);
            const query = billNos.length ? `&billNos=${encodeURIComponent(billNos.join(','))}` : '';
            const url = `/api/payments/getPaymentDetail?customerId=${customerId}${query}`;
            const {data} = await axiosInstance.get(url);
            return data;
        }
    };

    const handleViewDetail = async () => {
        try {
            if (!validateBeforePay()) return;
            const data = await getPaymentDetail();
            setBillDetail(data || {});
            setOpenDetail(true);
            setOpen(false);
        } catch {
            showErrorNotify('Không tải được chi tiết thanh toán.');
        }
    };

    const closeDetailDialog = () => {
        setBillDetail({});
        setOpenDetail(false);
        setOpen(true);
    };

    // Nhóm & in QR:
    // - QR mode → groupBill-old theo numOfMonth
    // - Legacy → groupBill theo billIds/billNos (giữ format cũ của bạn là billIds)
    const groupAndPrintQr = async () => {
        if (!validateBeforePay()) return;
        setIsLoading(true);
        try {
            let url = '';
            if (enableShowQr) {
                url = `/api/payments/groupBill-old?customerId=${customerId}&numOfMonth=${numOfMonth}`;
            } else {
                const ids = Array.from(selectedBillIds);
                url = `/api/payments/groupBill?customerId=${customerId}&billNos=${encodeURIComponent(ids.join(','))}`;
            }
            const {data} = await axiosInstance.get(url);
            if (data) {
                setQrInfo(data);
                setOpenQr(true);
                setOpen(false);
                // poll
                pollRef.current = setInterval(async () => {
                    try {
                        const rs = await axiosInstance.get(
                            `/api/payments/check?customerId=${customerId}&totalAmount=${data.totalAmount}&time=${data.createdTime}`
                        );
                        if (rs?.data?.paymentId) {
                            if (pollRef.current) {
                                clearInterval(pollRef.current);
                                pollRef.current = null;
                            }
                            await afterPaymentSuccess(rs.data.paymentId, true);
                            setOpenQr(false);
                        }
                    } catch {}
                }, 10000);
            } else {
                showErrorNotify('Có lỗi xảy ra. Vui lòng liên hệ quản trị viên!');
            }
        } catch {
            showErrorNotify('Có lỗi xảy ra. Vui lòng liên hệ quản trị viên!');
        } finally {
            setIsLoading(false);
        }
    };

    const closeQrDialog = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
        setOpenQr(false);
        setOpen(true);
        setQrInfo(null);
    };

    // table handlers
    const toggleExpand = (id: string) => {
        setExpandedRow((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleSelect = (id: string) => {
        setSelectedBillIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const allChecked = useMemo(
        () => bills.length > 0 && bills.every(b => selectedBillIds.has(b.billNo)),
        [bills, selectedBillIds]
    );

    const toggleSelectAll = () => {
        if (!bills.length) return;
        setSelectedBillIds((prev) => {
            const next = new Set(prev);
            if (allChecked) bills.forEach(b => next.delete(b.billNo));
            else bills.forEach(b => next.add(b.billNo));
            return next;
        });
    };

    const renderTable = () => {
        if (bills && bills.length > 0) {
            return (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {/* Ẩn cột checkbox khi ở QR mode */}
                            {!enableShowQr && (
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={selectedBillIds.size > 0 && !allChecked}
                                        checked={allChecked}
                                        onChange={toggleSelectAll}
                                        color="primary"
                                    />
                                </TableCell>
                            )}
                            <TableCell style={styles.nowrap}>Mã phiếu</TableCell>
                            <TableCell style={styles.nowrap}>Kỳ</TableCell>
                            <TableCell style={{...styles.right, ...styles.nowrap}}>Số tiền</TableCell>
                            <TableCell style={styles.nowrap} align="center">Chi tiết</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {bills.map((bill) => {
                            const checked = selectedBillIds.has(bill.billNo);
                            const expanded = expandedRow.has(bill.billNo);
                            return (
                                <Fragment key={bill.billNo}>
                                    <TableRow hover>
                                        {!enableShowQr && (
                                            <TableCell padding="checkbox">
                                                <Checkbox color="primary" checked={checked} onChange={() => toggleSelect(bill.billNo)}/>
                                            </TableCell>
                                        )}
                                        <TableCell>{bill.billNo}</TableCell>
                                        <TableCell>{bill.billMonth}</TableCell>
                                        <TableCell style={styles.right}>{formatNumber(bill.billBalance)}</TableCell>
                                        <TableCell align="center">
                                            <IconButton onClick={() => toggleExpand(bill.billNo)} size="small" aria-label="expand">
                                                {expanded ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>

                                    {!!bill.comment?.trim() && <TableRow >
                                        <TableCell colSpan={4} style={{fontWeight: 600}}>Ghi chú: {bill.comment}</TableCell>
                                    </TableRow>}

                                    <TableRow>
                                        <TableCell colSpan={enableShowQr ? 4 : 5} style={styles.collapseCell}>
                                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                                                {Array.isArray(bill.details) && bill.details.length > 0 ? (
                                                    <Table size="small">
                                                        <TableBody>
                                                            {bill.details.map((d, idx) => (
                                                                <TableRow key={d.billNo || idx}>
                                                                    <TableCell>{d.productName}</TableCell>
                                                                    <TableCell align="right">{d.quantity}</TableCell>
                                                                    <TableCell align="right">{formatNumber(d.totalAmount)}</TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                ) : (
                                                    <div style={styles.subtle}>Không có chi tiết dòng cho phiếu này.</div>
                                                )}
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            );
        }

        if (remainPaymentAmount) {
            return (
                <span>
          Số tiền thừa: {formatNumber(remainPaymentAmount)} <br/>
          Hiện chưa có phiếu cần thanh toán
        </span>
            );
        }

        if (customerId) {
            return <span>Hiện chưa có phiếu cần thanh toán</span>;
        }

        return null;
    };

    return (
        <Fragment>
            <Button onClick={openDialog} color="primary" variant="contained">
                <PaymentIcon style={{marginRight: 5}}/>Thanh toán
            </Button>

            <Dialog
                fullWidth
                maxWidth="xl"
                open={open}
                onClose={closeDialog}
                aria-label="Thanh toán"
                style={styles.suggestDialog}
            >
                <DialogTitle>Thanh toán khoản thu</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} style={styles.width100}>
                        <Grid item xs={12} sm={2} style={{marginRight: 30}}>
                            <TextField
                                label="Mã VinaID"
                                value={customerId}
                                disabled
                                fullWidth
                                required
                                margin="dense"
                            />
                            <TextField
                                label="Học sinh"
                                value={customerName}
                                disabled
                                fullWidth
                                margin="dense"
                            />
                            <TextField
                                label="Người thanh toán"
                                value={relationshipName}
                                disabled
                                fullWidth
                                required
                                margin="dense"
                            />
                            <TextField
                                label="Địa chỉ"
                                value={relationshipAddress}
                                onChange={(e) => setRelationshipAddress(e.target.value)}
                                fullWidth
                                margin="dense"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} style={{marginRight: 30}}>
                            {renderTable()}
                        </Grid>

                        <Grid item xs={12} sm={3}>
                            {bills?.length > 0 && (
                                <div style={{marginTop: 12}}>
                                    <PaymentMethodPicker
                                        source="paymentMethod"
                                        value={paymentMethod}
                                        onChange={(_, v) => setPaymentMethod(v)}
                                    />

                                    {/* Khi BANK_TRANSFER thì hiển thị gateway + ngày thanh toán */}
                                    {showGateway && (
                                        <TextField
                                            select
                                            SelectProps={{native: true}}
                                            label="Kênh thanh toán"
                                            value={gateway}
                                            onChange={(e) => setGateway(e.target.value)}
                                            fullWidth
                                            margin="dense"
                                        >
                                            <option value=""/>
                                            {(gateways || []).map((g) => (
                                                <option key={g.id || g.value || g.name} value={g.id || g.value || g.name}>
                                                    {g.name || g.label || g.id}
                                                </option>
                                            ))}
                                        </TextField>
                                    )}
                                    {showGateway && (
                                        <TextField
                                            label="Ngày thanh toán"
                                            type="date"
                                            InputLabelProps={{shrink: true}}
                                            value={paymentDate}
                                            onChange={(e) => setPaymentDate(e.target.value)}
                                            fullWidth
                                            margin="dense"
                                        />
                                    )}

                                    {/* QR mode: chọn số tháng */}
                                    {enableShowQr && bills?.length > 0 && (
                                        <TextField
                                            select
                                            SelectProps={{native: true}}
                                            label="Số phiếu thanh toán"
                                            value={numOfMonth}
                                            onChange={(e) => setNumOfMonth(Number(e.target.value))}
                                            fullWidth
                                            margin="dense"
                                        >
                                            {Array.from({length: bills.length}, (_, i) => i + 1).map(m => (
                                                <option key={m} value={m}>{m}</option>
                                            ))}
                                        </TextField>
                                    )}

                                    {bills?.length > 0 && (
                                        <NumberFormat
                                            thousandSeparator
                                            customInput={TextField}
                                            value={totalPayment}
                                            label="Số tiền thanh toán"
                                            required
                                            disabled
                                            fullWidth
                                            margin="dense"
                                        />
                                    )}
                                </div>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    {enableShowQr && (
                        <Button disabled={isLoading} variant="outlined" color="primary" onClick={groupAndPrintQr}>
                            Tạo QR
                        </Button>
                    )}

                    <Button disabled={isLoading} variant="contained" color="primary" onClick={handleViewDetail}>
                        Xem chi tiết
                    </Button>

                    <Popconfirm title="Bạn chắc chắn muốn thanh toán và in xác nhận thu?"
                                onConfirm={() => handlePay(true)}>
                        <Button disabled={isLoading} variant="contained" color="primary">
                            Thanh toán & In xác nhận thu
                        </Button>
                    </Popconfirm>

                    <Popconfirm title="Bạn chắc chắn muốn thanh toán?" onConfirm={() => handlePay(false)}>
                        <Button disabled={isLoading} variant="contained" color="primary">
                            <PaymentIcon style={{marginRight: 5}}/> Thanh toán
                        </Button>
                    </Popconfirm>

                    <Button onClick={closeDialog} startIcon={<IconCancel/>}>
                        Hủy
                    </Button>
                </DialogActions>
            </Dialog>

            <BillDetailDialog
                showDialogDetail={openDetail}
                billDetail={billDetail}
                handleCloseDialogDetail={closeDetailDialog}
            />

            <QrInfoDialog show={openQr} qrInfo={qrInfo} handleCloseDialog={closeQrDialog}/>
        </Fragment>
    );
};

export default connect(
    null,
    { refreshView }
)(PostQuickPayBillButton);
